import { useState } from 'react';
import './Item.css';

export default function Item({ item, onUpdate, onDelete, isHost, user, userProfile, mode, participants = [] }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(item.name);
    const isTaken = !!item.assignee;

    // Helper to get a stable color index based on creatorId
    const getUserColorIndex = (id) => {
        if (!id) return 0;
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            hash = id.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash % 6); // 6 color variants
    };

    const colorIndex = getUserColorIndex(item.creatorId);

    // Can edit ITEM NAME if:
    // 1. Host (Always)
    // 2. POTLUCK mode AND (Original creator OR Current assignee)
    // In SIGNUP mode, ONLY Host can edit item name. Guest can only assign themselves.
    const canEditName = isHost || (
        mode !== 'SIGNUP' && (
            (item.creatorId && item.creatorId === user?.uid) ||
            (item.assignee && userProfile && item.assignee === userProfile.name)
        )
    );

    // Can DELETE item if:
    // 1. Host (Always)
    // 2. POTLUCK mode AND (Original creator)
    // In SIGNUP mode, ONLY Host can delete items.
    const canDelete = isHost || (
        mode !== 'SIGNUP' &&
        (item.creatorId && item.creatorId === user?.uid)
    );

    const handleUpdateName = (e) => {
        e.preventDefault();
        if (editName.trim() && editName !== item.name) {
            onUpdate({ name: editName });
        }
        setIsEditing(false);
    };

    const handleAssign = (userId, userName) => {
        onUpdate({
            assignee: userName,
            assigneeUid: userId
        });
    };

    const handleUnassign = () => {
        onUpdate({
            assignee: '',
            assigneeUid: null
        });
    };

    // Filter participants for dropdown (Host only)
    const assignableParticipants = participants.filter(p => p.status === 'Yes' || p.status === 'Maybe');

    return (
        <div className={`item-row ${isTaken ? 'taken' : 'available'} user-color-${colorIndex}`}>
            <div className="item-info">
                {isEditing ? (
                    <form onSubmit={handleUpdateName} className="edit-item-name-form">
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                            className="edit-item-input"
                        />
                        <button type="submit" className="save-icon-btn">✓</button>
                        <button type="button" onClick={() => setIsEditing(false)} className="cancel-icon-btn">&times;</button>
                    </form>
                ) : (
                    <div className="item-name-container">
                        <span className="item-name">{item.name}</span>
                        {canEditName && <button onClick={() => setIsEditing(true)} className="edit-item-small-btn">✏️</button>}
                    </div>
                )}
            </div>

            <div className="item-actions">
                {isHost ? (
                    // Host View: Dropdown to assign anyone
                    <select
                        className="assignee-select"
                        value={item.assigneeUid || ''}
                        onChange={(e) => {
                            const selectedUid = e.target.value;
                            if (!selectedUid) {
                                handleUnassign();
                            } else {
                                const participant = participants.find(p => p.userId === selectedUid);
                                if (participant) {
                                    handleAssign(participant.userId, participant.name);
                                }
                            }
                        }}
                    >
                        <option value="">-- Unassigned --</option>
                        {assignableParticipants.map(p => (
                            <option key={p.id} value={p.userId}>
                                {p.name}
                            </option>
                        ))}
                        {/* Fallback if assigned user is not in list (e.g. deleted rsvp) */}
                        {item.assigneeUid && !assignableParticipants.find(p => p.userId === item.assigneeUid) && (
                            <option value={item.assigneeUid}>{item.assignee || 'Unknown User'}</option>
                        )}
                    </select>
                ) : mode === 'SIGNUP' ? (
                    // Guest View (Signup Mode)
                    item.assigneeUid === user?.uid ? (
                        <div className="assigned-status-row">
                            <span className="assigned-label">✅ You're bringing this</span>
                            <button onClick={handleUnassign} className="text-action-btn remove-me-btn">Remove Me</button>
                        </div>
                    ) : isTaken ? (
                        <span className="assigned-label locked">👤 {item.assignee}</span>
                    ) : (
                        <button onClick={() => handleAssign(user.uid, userProfile?.name)} className="signup-btn">
                            Sign Up
                        </button>
                    )
                ) : (
                    // Guest View (Potluck Mode) - Read Only auto-assigned
                    isTaken ? (
                        item.assigneeUid === user?.uid ? (
                            <span className="assigned-label mine">✅ You</span>
                        ) : (
                            <span className="assigned-label locked">👤 {item.assignee}</span>
                        )
                    ) : (
                        <span className="assigned-label">Free for taking</span> // Should be rare as guests auto-assign
                    )
                )}

                {canDelete && (
                    <button
                        className="delete-btn"
                        onClick={onDelete}
                        title="Remove item"
                    >
                        &times;
                    </button>
                )}
            </div>
        </div>
    );
}
